import { Injectable } from '@angular/core';
import {
    GlobalSettingsPayload,
    WebappAddressGlobalSettings,
    WebappContactsGlobalSettings,
    WebappGlobalSettings,
    WebappInstitutionalGlobalSettings,
    WebappSocialGlobalSettings
} from 'app/entities/global-settings';
import { BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { LocalStorageService } from './local-storage.service';

@Injectable({
    providedIn: 'root'
})
export class GlobalSettingsService {

    private readonly versionStorageKey = 'global_settings_version';
    private readonly etagStorageKey = 'global_settings_etag';
    private readonly webappStorageKey = 'global_settings_webapp';

    private readonly globalSettingsSubject = new BehaviorSubject<GlobalSettingsPayload | null>(null);

    readonly globalSettings$ = this.globalSettingsSubject.asObservable();
    readonly webappSettings$ = this.globalSettings$.pipe(
        map((settings) => settings?.webapp ?? null)
    );

    constructor(
        private localStorageService: LocalStorageService
    ) {
        const cachedSettings = this.loadFromCache();
        if (cachedSettings) {
            this.globalSettingsSubject.next(cachedSettings);
        }
    }

    get settings(): GlobalSettingsPayload | null {
        return this.globalSettingsSubject.value;
    }

    get version(): number | null {
        return this.settings?.version ?? null;
    }

    get etag(): string | null {
        return this.settings?.etag ?? null;
    }

    get webapp(): WebappGlobalSettings | null {
        return this.settings?.webapp ?? null;
    }

    get supportPhone(): string | null {
        return this.webapp?.contacts?.support_phone ?? null;
    }

    get supportWhatsappNumber(): string | null {
        return this.webapp?.contacts?.support_whatsapp_number ?? null;
    }

    get supportWhatsappUrl(): string | null {
        return this.webapp?.contacts?.support_whatsapp_url ?? null;
    }

    get termsUrl(): string | null {
        return this.webapp?.institutional?.terms_url ?? null;
    }

    get privacyUrl(): string | null {
        return this.webapp?.institutional?.privacy_url ?? null;
    }

    get helpCenterUrl(): string | null {
        return this.webapp?.institutional?.help_center_url ?? null;
    }

    get footerText(): string | null {
        return this.webapp?.institutional?.footer_text ?? null;
    }

    get socialLinks(): WebappSocialGlobalSettings {
        return this.webapp?.social ?? this.emptySocial();
    }

    hydrateFromAuthPayload(payload: any): boolean {
        const parsed = this.parseFromAuthPayload(payload);
        if (!parsed) {
            return false;
        }

        const currentSettings = this.settings;
        if (currentSettings && parsed.version <= currentSettings.version) {
            return false;
        }

        this.persistSettings(parsed);
        return true;
    }

    buildSupportWhatsappUrl(message?: string): string | null {
        const supportNumber = this.supportWhatsappNumber;
        if (supportNumber) {
            const url = new URL('https://api.whatsapp.com/send');
            url.searchParams.set('phone', supportNumber);
            if (message) {
                url.searchParams.set('text', message);
            }
            return url.toString();
        }

        const configuredUrl = this.supportWhatsappUrl;
        if (!configuredUrl) {
            return null;
        }

        if (!message) {
            return configuredUrl;
        }

        const separator = configuredUrl.includes('?') ? '&' : '?';
        return `${configuredUrl}${separator}text=${encodeURIComponent(message)}`;
    }

    private persistSettings(settings: GlobalSettingsPayload): void {
        this.globalSettingsSubject.next(settings);
        this.localStorageService.setItem(this.versionStorageKey, settings.version);
        this.localStorageService.setItem(this.etagStorageKey, settings.etag);
        this.localStorageService.setItem(this.webappStorageKey, settings.webapp);
    }

    private loadFromCache(): GlobalSettingsPayload | null {
        const rawVersion = this.localStorageService.getItem(this.versionStorageKey);
        const rawEtag = this.localStorageService.getItem(this.etagStorageKey);
        const rawWebapp = this.localStorageService.getItem(this.webappStorageKey);

        return this.parseGlobalSettings({
            version: rawVersion,
            etag: rawEtag,
            webapp: rawWebapp
        });
    }

    private parseFromAuthPayload(payload: any): GlobalSettingsPayload | null {
        if (!payload || typeof payload !== 'object') {
            return null;
        }

        const rawGlobalSettings = payload?.data?.global_settings ?? payload?.global_settings ?? null;
        return this.parseGlobalSettings(rawGlobalSettings);
    }

    private parseGlobalSettings(rawGlobalSettings: any): GlobalSettingsPayload | null {
        if (!rawGlobalSettings || typeof rawGlobalSettings !== 'object') {
            return null;
        }

        const parsedVersion = this.parseVersion(rawGlobalSettings.version);
        if (parsedVersion === null) {
            return null;
        }

        const parsedEtag = this.parseString(rawGlobalSettings.etag);
        if (!parsedEtag) {
            return null;
        }

        const webapp = this.parseWebapp(rawGlobalSettings.webapp);
        if (!webapp) {
            return null;
        }

        return {
            version: parsedVersion,
            etag: parsedEtag,
            webapp: webapp
        };
    }

    private parseWebapp(rawWebapp: any): WebappGlobalSettings | null {
        if (!rawWebapp || typeof rawWebapp !== 'object') {
            return null;
        }

        return {
            contacts: this.parseContacts(rawWebapp.contacts),
            address: this.parseAddress(rawWebapp.address),
            social: this.parseSocial(rawWebapp.social),
            institutional: this.parseInstitutional(rawWebapp.institutional),
        };
    }

    private parseContacts(rawContacts: any): WebappContactsGlobalSettings {
        return {
            support_phone: this.parseString(rawContacts?.support_phone),
            support_whatsapp_number: this.parseString(rawContacts?.support_whatsapp_number),
            support_whatsapp_url: this.parseString(rawContacts?.support_whatsapp_url),
        };
    }

    private parseAddress(rawAddress: any): WebappAddressGlobalSettings {
        return {
            street: this.parseString(rawAddress?.street),
            number: this.parseString(rawAddress?.number),
            complement: this.parseString(rawAddress?.complement),
            neighborhood: this.parseString(rawAddress?.neighborhood),
            city: this.parseString(rawAddress?.city),
            state: this.parseString(rawAddress?.state),
            zipcode: this.parseString(rawAddress?.zipcode),
        };
    }

    private parseSocial(rawSocial: any): WebappSocialGlobalSettings {
        return {
            facebook_url: this.parseString(rawSocial?.facebook_url),
            instagram_url: this.parseString(rawSocial?.instagram_url),
            linkedin_url: this.parseString(rawSocial?.linkedin_url),
            youtube_url: this.parseString(rawSocial?.youtube_url),
            x_url: this.parseString(rawSocial?.x_url),
        };
    }

    private parseInstitutional(rawInstitutional: any): WebappInstitutionalGlobalSettings {
        return {
            footer_text: this.parseString(rawInstitutional?.footer_text),
            terms_url: this.parseString(rawInstitutional?.terms_url),
            privacy_url: this.parseString(rawInstitutional?.privacy_url),
            help_center_url: this.parseString(rawInstitutional?.help_center_url),
        };
    }

    private parseVersion(value: any): number | null {
        if (typeof value === 'number' && Number.isFinite(value)) {
            return value;
        }

        if (typeof value === 'string' && value.trim().length > 0) {
            const parsed = Number(value);
            return Number.isFinite(parsed) ? parsed : null;
        }

        return null;
    }

    private parseString(value: any): string | null {
        return typeof value === 'string' && value.trim().length > 0 ? value : null;
    }

    private emptySocial(): WebappSocialGlobalSettings {
        return {
            facebook_url: null,
            instagram_url: null,
            linkedin_url: null,
            youtube_url: null,
            x_url: null
        };
    }
}
